/**
 * One Mahjong table, shared by Belajar, Solo and Online.
 *
 * The table is not a panel on a page; it is the screen. It fills the stage the
 * shell gives it, and it is built from one idea: a piece of furniture you are
 * sitting at. The felt runs edge to edge and off the near side of the screen,
 * where it becomes the rail your own tiles stand on - so the hand is not a
 * widget below a picture of a table, it is the near edge of the table.
 *
 *   .felt        the recessed surface, bleeding to the viewport edges
 *   .felt__plane the tilted plane, and the only place a transform lives
 *   .dock        the near rail: the decision, the claims, your hand
 *
 * Everything physical lives on the plane - the four walls, the discard field
 * inside them, the seats around the outside - so objects really do sit above
 * the felt rather than beside it.
 *
 * Everything it draws comes from `TableModel`, which is the same shape whether
 * the state came from the local engine or from a redacted multiplayer view -
 * so this component cannot show anything the player is not allowed to see.
 */

import { useEffect, useState } from "react";
import type { Action, PlayerId, TileKind } from "@kikik/mahjong-core";
import { useI18n } from "../i18n";
import type { TableModel } from "../game/tableModel";
import { seatSide } from "../game/tableModel";
import { ActionBar } from "./ActionBar";
import { CenterBoard } from "./CenterBoard";
import { ClaimBar } from "./ClaimBar";
import { DiscardField } from "./DiscardField";
import { PlayerHandPanel } from "./PlayerHandPanel";
import { OpeningChrome, OpeningScene, useOpeningCeremony } from "./OpeningCeremony";
import { SeatPanel } from "./SeatPanel";
import { SelfSeat } from "./SelfSeat";
import { TableWall } from "./TableWall";
import { readOpeningPreference } from "../game/openingPreference";
import { useTableSound } from "../game/useTableSound";

export interface GameTableProps {
  model: TableModel;
  nameFor: (playerId: PlayerId) => string;
  waits: readonly TileKind[];
  hintTileId: number | null;
  onSubmit: (action: Action) => void;
  /** Tutorial keeps draw as an explicit lesson action; normal modes automate it. */
  manualDraw?: boolean;
  onHint?: () => void;
  /** The coach's recommendation, highlighted in the claim bar when it is one. */
  suggested?: Action | null;
  statusExtra?: string;
  children?: React.ReactNode;
  /** Omitted for tutorial scenarios; changed only when a normal new game starts. */
  ceremonyKey?: string | number;
}

export function GameTable({
  model,
  nameFor,
  waits,
  hintTileId,
  onSubmit,
  manualDraw = false,
  onHint,
  suggested,
  statusExtra,
  children,
  ceremonyKey,
}: GameTableProps) {
  const { t } = useI18n();
  const opponents = model.seats.filter((seat) => !seat.isYou);
  const draw = model.legalActions.find((action) => action.type === "draw");
  const waitingOnYou = model.pendingClaims?.waitingOn.includes(model.you) ?? false;
  // Once the round is over nobody is to move, so no seat wears the turn ring.
  const live = model.result === null;
  const yourMove = live && (waitingOnYou || (model.turn === model.you && !draw));

  /*
   * The preference now lives in the menu, so it is read again whenever a round
   * starts rather than held as the table's own state. That is the only instant
   * it can change anything: a ceremony already playing is not reconfigured
   * underneath the player.
   */
  const [openingPreference, setOpeningPreference] = useState(readOpeningPreference);
  useEffect(() => {
    setOpeningPreference(readOpeningPreference());
  }, [ceremonyKey]);

  /*
   * The ceremony's clock lives here rather than inside one component, because
   * the ceremony is not one box any more. Its physical half mounts inside the
   * pit so that it shares the wall's own geometry; its caption sits flat over
   * the table; and the table itself has to know the beat, because the real
   * wall, the opponents' racks and your own hand are held back until the deal
   * reaches them. `data-opening` is how the stylesheet is told.
   */
  const opening = useOpeningCeremony(ceremonyKey, openingPreference);
  const ceremonyRunning = opening.stage !== "done";

  useTableSound(model);

  const hint = !live
    ? t("table.hint.ended")
    : waitingOnYou
      ? t("table.hint.claimable")
      : model.phase === "awaiting-claims"
        ? t("table.hint.waitingClaims")
        : model.turn !== model.you
          ? t("table.hint.waitingPlayer", { player: nameFor(model.turn) })
          : draw
            ? manualDraw
              ? t("table.hint.draw")
              : t("table.hint.drawing")
            : t("table.hint.discard");

  return (
    <div
      className="table"
      data-testid="mahjong-table"
      data-phase={model.phase}
      data-your-move={yourMove ? "true" : "false"}
      {...(ceremonyRunning ? { "data-opening": opening.stage } : {})}
    >
      <div className="felt" role="group" aria-label={t("table.region")}>
        <div className="felt__grain" aria-hidden="true" />
        <div className="felt__glow" aria-hidden="true" />

        <div className="felt__plane">
          {opponents.map((seat) => (
            <SeatPanel
              key={seat.id}
              seat={seat}
              name={nameFor(seat.id)}
              side={seatSide(seat.id, model.you)}
              isTurn={live && model.turn === seat.id && model.phase !== "awaiting-claims"}
              waiting={model.pendingClaims?.waitingOn.includes(seat.id) ?? false}
              isDealer={model.dealer === seat.id}
            />
          ))}

          <SelfSeat model={model} nameFor={nameFor} />

          <div className="pit">
            <div className="pit__inner">
              <TableWall
                wallRemaining={model.wallRemaining}
                deadWallRemaining={model.deadWallRemaining}
                dealer={model.dealer}
                you={model.you}
              />
              <DiscardField model={model} nameFor={nameFor} />
            </div>
            {/* The ceremony's physical half, inside the pit so it measures
                itself against the same wall the real one does. */}
            <OpeningScene stage={opening.stage} dice={opening.dice} />
          </div>
        </div>

        <CenterBoard
          model={model}
          nameFor={nameFor}
          {...(statusExtra ? { extra: statusExtra } : {})}
        />
      </div>

      {/*
        * The near rail. It carries the same wood as the frame around the felt
        * and meets it with no gap, so it reads as the edge of the table rather
        * than a second panel underneath one - and it is pinned to the bottom
        * of the stage, so the hand and the next action can never fall below
        * the fold the way they did at 1024x768 and on every phone.
        */}
      <div className="dock" data-testid="player-rack">
        {/* Mode-specific instruction leads the rail: a learner's task is the
            first thing to read on the way down to their own tiles, not a line
            stranded under them at the bottom edge of the screen. */}
        {children}
        <ClaimBar model={model} onClaim={onSubmit} suggested={suggested ?? null} />
        <ActionBar
          model={model}
          hint={hint}
          onSubmit={onSubmit}
          manualDraw={manualDraw}
          {...(onHint ? { onHint } : {})}
        />
        <PlayerHandPanel
          model={model}
          hintTileId={hintTileId}
          waits={waits}
          onDiscard={(tileId) => onSubmit({ type: "discard", playerId: model.you, tileId })}
        />
      </div>

      <OpeningChrome
        stage={opening.stage}
        dice={opening.dice}
        dealerName={nameFor(model.dealer)}
        turnName={nameFor(model.turn)}
        onSkip={opening.skip}
      />
    </div>
  );
}
